import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  ApiSuccess,
  IssueReactivationBoletoInput,
  PaginatedResponse,
  ReactivationBillingFilters,
  ReactivationBillingRequest,
} from '@alamo/types';

import { apiClient } from '../../client';
import { queryKeys } from '../../query-keys';

export function useReactivationBilling(filters?: ReactivationBillingFilters) {
  return useQuery({
    queryKey: queryKeys.reactivationBilling.list(filters as Record<string, unknown> | undefined),
    queryFn: async () => {
      const response = await apiClient.get<
        ApiSuccess<{ count: number; rows: ReactivationBillingRequest[] }>
      >('/admin/reactivation-billing', { params: filters });
      const raw = response.data.data;
      const limit = filters?.limit ?? 20;
      return {
        items: raw.rows,
        total: raw.count,
        page: filters?.page ?? 1,
        limit,
        totalPages: Math.ceil(raw.count / limit),
      } satisfies PaginatedResponse<ReactivationBillingRequest>;
    },
  });
}

export function useReactivationBillingOpenCount(options?: {
  enabled?: boolean;
  refetchInterval?: number;
}) {
  return useQuery({
    queryKey: queryKeys.reactivationBilling.openCount(),
    queryFn: async () => {
      const response = await apiClient.get<ApiSuccess<{ count: number }>>(
        '/admin/reactivation-billing/open-count',
      );
      return response.data.data;
    },
    enabled: options?.enabled,
    refetchInterval: options?.refetchInterval,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

export function useReactivationBillingRequest(id: string) {
  return useQuery({
    queryKey: queryKeys.reactivationBilling.detail(id),
    queryFn: async () => {
      const response = await apiClient.get<ApiSuccess<ReactivationBillingRequest>>(
        `/admin/reactivation-billing/${id}`,
      );
      return response.data.data;
    },
    enabled: Boolean(id),
  });
}

export function useIssueReactivationBoleto(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: IssueReactivationBoletoInput) => {
      const response = await apiClient.post<ApiSuccess<ReactivationBillingRequest>>(
        `/admin/reactivation-billing/${id}/issue`,
        input,
      );
      return response.data.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.reactivationBilling.all });
    },
  });
}

export function useStartReactivationBilling(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<ApiSuccess<ReactivationBillingRequest>>(
        `/admin/reactivation-billing/${id}/start`,
      );
      return response.data.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.reactivationBilling.all });
    },
  });
}
